import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { getCashflow, getCashflowScenario, getContractBacklog, getReceivables, trackUserEvent } from '../../services/api';
import { logger } from '../../utils/logger';
import ChatDrawer from '../ChatDrawer/ChatDrawer';
import CashflowChart from './CashflowChart';
import ContractBacklogChart from './ContractBacklogChart';
import OutstandingList from './OutstandingList';
import { useConversation } from '../../hooks/useConversation';
import { readAgentOpen, readOverview, writeAgentOpen, writeOverview } from '../../utils/workspaceState';
import './OverviewView.css';
import './CashflowChart.css';

const RECEIVABLES_MAX_AGE_DAYS = [730, 365, 182, 91];

const toIsoDate = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

/**
 * Historic from_date for backlog range (months ago from today).
 * @param {number} months
 * @returns {string} YYYY-MM-DD
 */
const backlogFromDate = (months) => {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  d.setMonth(d.getMonth() - months);
  return toIsoDate(d);
};

/**
 * OverviewView component - main container for overview page
 * Uses shared conversation state (no automatic opening message).
 */
const OverviewView = () => {
  const { t } = useTranslation();
  const { activeCompany, user } = useAuth();
  const userId = user?.id;
  const companyId = activeCompany?.id;
  const [initialOverview] = useState(() => readOverview(userId, companyId));
  const [chartMode, setChartMode] = useState(initialOverview.chartMode);
  const [cashflowDays, setCashflowDays] = useState(initialOverview.cashflowDays);
  const [includeNonInvoicedWork, setIncludeNonInvoicedWork] = useState(initialOverview.includeNonInvoicedWork);
  const [includeOverdueInvoices, setIncludeOverdueInvoices] = useState(initialOverview.includeOverdueInvoices);

  const [cashflowData, setCashflowData] = useState(null);
  const [cashflowLoading, setCashflowLoading] = useState(true);
  const [cashflowError, setCashflowError] = useState(null);

  const [scenarioData, setScenarioData] = useState(null);
  const [scenarioBaseline, setScenarioBaseline] = useState(null);
  const [scenarioError, setScenarioError] = useState(null);

  const [backlogData, setBacklogData] = useState(null);
  const [backlogLoading, setBacklogLoading] = useState(false);
  const [backlogError, setBacklogError] = useState(null);
  const [backlogMonths, setBacklogMonths] = useState(initialOverview.backlogMonths);

  const [receivablesData, setReceivablesData] = useState(null);
  const [receivablesLoading, setReceivablesLoading] = useState(false);
  const [receivablesError, setReceivablesError] = useState(null);
  const [receivablesMaxAgeDays, setReceivablesMaxAgeDays] = useState(initialOverview.receivablesMaxAgeDays);
  const [receivablesSortColumn, setReceivablesSortColumn] = useState(initialOverview.receivablesSortColumn);
  const [receivablesSortDirection, setReceivablesSortDirection] = useState(initialOverview.receivablesSortDirection);

  const [isChatDrawerOpen, setIsChatDrawerOpen] = useState(() => readAgentOpen(userId));
  const [hydratedFor, setHydratedFor] = useState(
    userId && companyId ? `${userId}:${companyId}` : null
  );
  const conversation = useConversation();

  // Core cashflow. Stays on screen while a scenario request is in flight.
  useEffect(() => {
    if (!activeCompany?.id) return;

    const ac = new AbortController();
    const signal = ac.signal;

    const fetchCashflow = async () => {
      setCashflowLoading(true);
      setCashflowError(null);
      setCashflowData(null);

      try {
        logger.info(`Fetching cashflow data for company ${activeCompany.id}`);
        const response = await getCashflow(activeCompany.id, null, null, signal);

        if (signal.aborted) return;
        if (response && response.data) {
          setCashflowData(response.data);
          logger.info(`Loaded cashflow data for ${response.data.length} date(s)`);
        } else {
          setCashflowData([]);
          logger.warn('Unexpected response format from cashflow API');
        }
      } catch (err) {
        if (signal.aborted || err.name === 'AbortError') return;
        logger.error('Error fetching cashflow data:', err);
        setCashflowError(err.message || t('overview.error'));
      } finally {
        if (!signal.aborted) setCashflowLoading(false);
      }
    };

    fetchCashflow();
    return () => ac.abort();
  }, [activeCompany?.id, t]);

  // Scenario days and the guide line come from the same response.
  useEffect(() => {
    if (!activeCompany?.id) return;

    const eventTypes = [
      ...(includeNonInvoicedWork ? ['projected_outbound_invoice'] : []),
      ...(includeOverdueInvoices ? ['overdue_outbound_invoice'] : []),
    ];
    if (!eventTypes.length) {
      setScenarioData(null);
      setScenarioBaseline(null);
      setScenarioError(null);
      return;
    }

    const ac = new AbortController();
    const signal = ac.signal;

    // Keep the scenario already on screen until this response arrives.
    const fetchScenario = async () => {
      setScenarioError(null);

      try {
        logger.info(
          `Fetching cashflow scenario for company ${activeCompany.id} (event_types=${eventTypes.join(',')})`
        );
        const response = await getCashflowScenario(activeCompany.id, eventTypes, signal);

        if (signal.aborted) return;
        if (response && response.data && Array.isArray(response.baseline)) {
          setScenarioData(response.data);
          setScenarioBaseline(response.baseline);
          logger.info(
            `Loaded cashflow scenario for ${response.data.length} date(s), ` +
              `baseline ${response.baseline.length} date(s)`
          );
        } else {
          setScenarioData([]);
          setScenarioBaseline([]);
          logger.warn('Unexpected response format from cashflow scenario API');
        }
      } catch (err) {
        if (signal.aborted || err.name === 'AbortError') return;
        logger.error('Error fetching cashflow scenario:', err);
        setScenarioError(err.message || t('overview.error'));
      }
    };

    fetchScenario();
    return () => ac.abort();
  }, [activeCompany?.id, includeNonInvoicedWork, includeOverdueInvoices, t]);

  // Load this company's overview setup and the shared agent flag. Writes stay
  // gated on hydratedFor so a company switch cannot save the previous company's
  // values under the new id.
  useEffect(() => {
    if (!userId || !companyId) return;
    const overview = readOverview(userId, companyId);
    setChartMode(overview.chartMode);
    setCashflowDays(overview.cashflowDays);
    setIncludeNonInvoicedWork(overview.includeNonInvoicedWork);
    setIncludeOverdueInvoices(overview.includeOverdueInvoices);
    setBacklogMonths(overview.backlogMonths);
    setReceivablesMaxAgeDays(overview.receivablesMaxAgeDays);
    setReceivablesSortColumn(overview.receivablesSortColumn);
    setReceivablesSortDirection(overview.receivablesSortDirection);
    setIsChatDrawerOpen(readAgentOpen(userId));
    setHydratedFor(`${userId}:${companyId}`);
  }, [userId, companyId]);

  useEffect(() => {
    if (!userId || !companyId) return;
    if (hydratedFor !== `${userId}:${companyId}`) return;
    writeOverview(userId, companyId, {
      chartMode,
      cashflowDays,
      receivablesMaxAgeDays,
      receivablesSortColumn,
      receivablesSortDirection,
      backlogMonths,
      includeNonInvoicedWork,
      includeOverdueInvoices,
    });
    writeAgentOpen(userId, isChatDrawerOpen);
  }, [
    hydratedFor,
    userId,
    companyId,
    chartMode,
    cashflowDays,
    receivablesMaxAgeDays,
    receivablesSortColumn,
    receivablesSortDirection,
    backlogMonths,
    includeNonInvoicedWork,
    includeOverdueInvoices,
    isChatDrawerOpen,
  ]);

  // Drop the previous company's fetched series. Preferences come from storage.
  useEffect(() => {
    setScenarioData(null);
    setScenarioBaseline(null);
    setScenarioError(null);
    setBacklogData(null);
    setBacklogError(null);
    setReceivablesData(null);
    setReceivablesError(null);
  }, [activeCompany?.id]);
  // Lazy-fetch contract backlog when mode is backlog (and on range/company change)
  useEffect(() => {
    if (chartMode !== 'contract_backlog' || !activeCompany?.id) return;

    const ac = new AbortController();
    const signal = ac.signal;

    const fetchBacklog = async () => {
      setBacklogLoading(true);
      setBacklogError(null);

      try {
        const fromDate = backlogFromDate(backlogMonths);
        const toDate = toIsoDate(new Date());
        logger.info(
          `Fetching contract backlog for company ${activeCompany.id} (${fromDate}..${toDate})`
        );
        const response = await getContractBacklog(
          activeCompany.id,
          fromDate,
          toDate,
          signal
        );

        if (signal.aborted) return;
        if (response && response.data) {
          setBacklogData(response.data);
          logger.info(`Loaded contract backlog for ${response.data.length} date(s)`);
        } else {
          setBacklogData([]);
          logger.warn('Unexpected response format from getContractBacklog');
        }
      } catch (err) {
        if (signal.aborted || err.name === 'AbortError') return;
        logger.error('Error fetching contract backlog:', err);
        setBacklogError(err.message || t('overview.backlogError'));
      } finally {
        if (!signal.aborted) setBacklogLoading(false);
      }
    };

    fetchBacklog();
    return () => ac.abort();
  }, [chartMode, backlogMonths, activeCompany?.id, t]);

  useEffect(() => {
    if (chartMode !== 'receivables' || !activeCompany?.id) return;

    const ac = new AbortController();
    const signal = ac.signal;

    const fetchReceivables = async () => {
      setReceivablesLoading(true);
      setReceivablesError(null);

      try {
        logger.info(`Fetching receivables for company ${activeCompany.id}`);
        const response = await getReceivables(
          activeCompany.id,
          receivablesMaxAgeDays,
          signal
        );

        if (signal.aborted) return;
        if (response && Array.isArray(response.customers)) {
          setReceivablesData(response.customers);
          logger.info(`Loaded receivables for ${response.customers.length} customer(s)`);
        } else {
          setReceivablesData([]);
          logger.warn('Unexpected response format from getReceivables');
        }
      } catch (err) {
        if (signal.aborted || err.name === 'AbortError') return;
        logger.error('Error fetching receivables:', err);
        setReceivablesError(err.message || t('overview.receivablesError'));
      } finally {
        if (!signal.aborted) setReceivablesLoading(false);
      }
    };

    fetchReceivables();
    return () => ac.abort();
  }, [chartMode, receivablesMaxAgeDays, activeCompany?.id, t]);

  const handleModeChange = useCallback((mode) => {
    if (mode === chartMode) return;
    setChartMode(mode);
    if (mode === 'contract_backlog' && activeCompany?.id) {
      trackUserEvent({
        companyId: activeCompany.id,
        event: 'view_contract_backlog',
        operation: 'read',
        path: '/overview/contract-backlog'
      });
    }
    if (mode === 'receivables' && activeCompany?.id) {
      trackUserEvent({
        companyId: activeCompany.id,
        event: 'view_receivables',
        operation: 'read',
        path: '/overview/receivables'
      });
    }
  }, [chartMode, activeCompany?.id]);

  const renderRangeControls = () => {
    if (chartMode === 'cashflow') {
      return (
        <select
          id="cashflow-range-select"
          className="overview-range-select"
          value={cashflowDays}
          onChange={(event) => setCashflowDays(Number(event.target.value))}
          aria-label={t('overview.viewRange')}
        >
          {[30, 45, 60].map((days) => (
            <option key={days} value={days}>
              {days} {t('overview.days')}
            </option>
          ))}
        </select>
      );
    }

    if (chartMode === 'receivables') {
      return (
        <select
          id="receivables-age-select"
          className="overview-range-select"
          value={receivablesMaxAgeDays}
          onChange={(event) => setReceivablesMaxAgeDays(Number(event.target.value))}
          aria-label={t('overview.viewRange')}
        >
          {RECEIVABLES_MAX_AGE_DAYS.map((days) => (
            <option key={days} value={days}>
              {t(`overview.receivablesAge_${days}`)}
            </option>
          ))}
        </select>
      );
    }

    if (chartMode !== 'contract_backlog') return null;

    return (
      <select
        id="backlog-range-select"
        className="overview-range-select"
        value={backlogMonths}
        onChange={(event) => setBacklogMonths(Number(event.target.value))}
        aria-label={t('overview.viewRange')}
      >
        {[3, 6, 12].map((months) => (
          <option key={months} value={months}>
            {t(`overview.months_${months}`)}
          </option>
        ))}
      </select>
    );
  };

  const renderScenarioToggle = () => {
    if (chartMode !== 'cashflow') return null;

    return (
      <div className="cashflow-scenario-toggles">
        <button
          type="button"
          role="switch"
          aria-checked={includeNonInvoicedWork}
          title={t('overview.includeNonInvoicedWorkExplanation')}
          className={`cashflow-scenario-toggle ${includeNonInvoicedWork ? 'on' : ''}`}
          onClick={() => setIncludeNonInvoicedWork((prev) => !prev)}
        >
          <span className="cashflow-switch" aria-hidden="true">
            <span className="cashflow-switch-thumb" />
          </span>
          <span className="cashflow-scenario-toggle-label">
            {t('overview.includeNonInvoicedWork')}
          </span>
        </button>
        <button
          type="button"
          role="switch"
          aria-checked={includeOverdueInvoices}
          title={t('overview.includeOverdueInvoicesExplanation')}
          className={`cashflow-scenario-toggle ${includeOverdueInvoices ? 'on' : ''}`}
          onClick={() => setIncludeOverdueInvoices((prev) => !prev)}
        >
          <span className="cashflow-switch" aria-hidden="true">
            <span className="cashflow-switch-thumb" />
          </span>
          <span className="cashflow-scenario-toggle-label">
            {t('overview.includeOverdueInvoices')}
          </span>
        </button>
      </div>
    );
  };

  const renderChartBody = () => {
    if (chartMode === 'cashflow') {
      const scenarioActive = includeNonInvoicedWork || includeOverdueInvoices;
      if (cashflowLoading && !cashflowData) {
        return (
          <div className="overview-loading">
            <div className="loading-spinner"></div>
            <p>{t('overview.loading')}</p>
          </div>
        );
      }
      if (scenarioActive && scenarioError && !scenarioData) {
        return (
          <div className="overview-error">
            <p className="error-message">⚠️ {scenarioError}</p>
          </div>
        );
      }
      if (cashflowError && !cashflowData) {
        return (
          <div className="overview-error">
            <p className="error-message">⚠️ {cashflowError}</p>
          </div>
        );
      }
      const chartData = scenarioActive && scenarioData ? scenarioData : cashflowData;
      const chartBaseline = scenarioActive && scenarioData ? scenarioBaseline : null;
      if (chartData && chartData.length > 0) {
        return (
          <CashflowChart
            data={chartData}
            baseline={chartBaseline}
            selectedDays={cashflowDays}
          />
        );
      }
      return (
        <div className="overview-no-data">
          <p>{t('overview.noData')}</p>
        </div>
      );
    }

    if (chartMode === 'receivables') {
      if (receivablesLoading && !receivablesData) {
        return (
          <div className="overview-loading">
            <div className="loading-spinner"></div>
            <p>{t('overview.receivablesLoading')}</p>
          </div>
        );
      }
      if (receivablesError && !receivablesData) {
        return (
          <div className="overview-error">
            <p className="error-message">⚠️ {receivablesError}</p>
          </div>
        );
      }
      if (receivablesData && receivablesData.length > 0) {
        return (
          <OutstandingList
            customers={receivablesData}
            sortColumn={receivablesSortColumn}
            sortDirection={receivablesSortDirection}
            onSortChange={(column, direction) => {
              setReceivablesSortColumn(column);
              setReceivablesSortDirection(direction);
            }}
          />
        );
      }
      return (
        <div className="overview-no-data">
          <p>{t('overview.receivablesNoData')}</p>
        </div>
      );
    }

    if (backlogLoading && !backlogData) {
      return (
        <div className="overview-loading">
          <div className="loading-spinner"></div>
          <p>{t('overview.backlogLoading')}</p>
        </div>
      );
    }
    if (backlogError && !backlogData) {
      return (
        <div className="overview-error">
          <p className="error-message">⚠️ {backlogError}</p>
        </div>
      );
    }
    if (backlogData && backlogData.length > 0) {
      return <ContractBacklogChart data={backlogData} />;
    }
    return (
      <div className="overview-no-data">
        <p>{t('overview.backlogNoData')}</p>
      </div>
    );
  };

  return (
    <div className="overview-view-container">
      <div className="overview-view">
        <div className="overview-header">
          {activeCompany && <h1 className="header-company-name">{activeCompany.name}</h1>}
        </div>

        <div className="overview-chart-header">
          <div className="overview-chart-header-left">
            <div className="overview-mode-tabs" role="tablist" aria-label={t('overview.modeLabel')}>
              <button
                type="button"
                role="tab"
                aria-selected={chartMode === 'cashflow'}
                className={`overview-mode-tab ${chartMode === 'cashflow' ? 'active' : ''}`}
                onClick={() => handleModeChange('cashflow')}
              >
                {t('overview.modeCashflow')}
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={chartMode === 'receivables'}
                className={`overview-mode-tab ${chartMode === 'receivables' ? 'active' : ''}`}
                onClick={() => handleModeChange('receivables')}
              >
                {t('overview.modeReceivables')}
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={chartMode === 'contract_backlog'}
                className={`overview-mode-tab ${chartMode === 'contract_backlog' ? 'active' : ''}`}
                onClick={() => handleModeChange('contract_backlog')}
              >
                {t('overview.modeContractBacklog')}
              </button>
            </div>
          </div>
          <div className="overview-chart-header-right">
            {renderRangeControls()}
            {renderScenarioToggle()}
          </div>
        </div>

        <div className="overview-content-wrapper">
          {renderChartBody()}
        </div>
      </div>

      <ChatDrawer
        isOpen={isChatDrawerOpen}
        onToggle={() => setIsChatDrawerOpen(!isChatDrawerOpen)}
        context="overview"
        conversation={conversation}
      />
    </div>
  );
};

export default OverviewView;
